/**
 * The PDApp's SETTINGS TAB in the share viewer (#757) — MiniAppEditor's
 * 'settings' face (`requestMiniappView(id, 'settings')` in the app), rendered
 * in place instead of navigating to the recipient's drive.
 *
 * It is the real settings surface, `MiniAppSettingsModifier`, unmodified. Its
 * data calls go to `/api/pdapp/*` (+ `/api/files` for the folder picker) through
 * the api-client, which means: the recipient's OWN cookie session and the
 * share chrome's EFFECTIVE identity — a `?tenant=`/`?orgEmail=` override, else
 * best access (scoped onto the api-client for this tab's lifetime — see the
 * component). That is not necessarily the account picker's ✓ (which follows
 * the bound app session), so the tab names the identity it manages as. The share session header the
 * chrome installs on the api-client is ignored there — those routes don't mount
 * the share-session auth — so a link grant confers nothing: the server's own
 * manage gate (`requireMiniappManage`) decides every read and write, and a 403
 * renders the modifier's "can't manage" state. The chrome only OFFERS this tab
 * when the share resolver, asked under that same identity, reports native write
 * on the app folder (`pdappCanManage`).
 *
 * KNOWN GAP (accepted): the modifier's live refresh rides the page's realtime
 * socket, which on `/share-view` is bound to the viewer's PERSONAL tenant — so
 * for an ORG-owned app, another admin's concurrent grant/trigger change doesn't
 * appear until this tab reloads its data (its own edits refresh normally).
 * Org apps had no Settings here at all before; re-binding the socket per tab is
 * a larger change than this surface warrants.
 *
 * `suppressInstallGate` as in MiniAppEditor: settings configures the app; the
 * install consent point belongs to the runner.
 */
import { useLayoutEffect, useState } from 'react'
import { overrideActiveIdentity } from '@papan/api-client'
import { MiniAppSettingsModifier } from '@papan/collab/ui'

/** The routes the settings surface calls, all of which resolve the folder in
 *  the REQUEST's tenant: miniappApi (`/api/miniapp`, `/api/pdapp`), the grant
 *  picker (`/api/files` lists/breadcrumbs/search, `/api/workspaces` for its
 *  Projects/PDApps places), chain create/delete, and the debug log + run
 *  history (`/api/debug-log`, `/api/app-run-log`). NOT `/api/files/write-status`
 *  — the page's WriteStatusProvider polls it for the viewer's own tenant.
 *  Exported so a test pins the list against the calls. */
export const settingsPath = (path: string): boolean =>
  path.startsWith('/api/miniapp/') || path.startsWith('/api/pdapp/') ||
  path.startsWith('/api/debug-log') || path.startsWith('/api/app-run-log') ||
  path === '/api/workspaces' || path === '/api/workspaces/recent' ||
  (path.startsWith('/api/files') && !path.startsWith('/api/files/write-status'))

export function SharePdappSettings({ folderId, folderName, identity }: {
  folderId: string
  folderName: string | null
  /** The chrome's EFFECTIVE identity — the one the Settings gate asked under.
   *  On `/share-view/…` the api-client would otherwise send the viewer's
   *  PERSONAL tenant, and an org-owned app's settings would 404/403 for its own
   *  admins. Scoped to this tab's lifetime; the server re-checks manage rights.
   *  Shown at the top of the tab ("Managing as …"), because it can differ from
   *  the identity the account picker ticks. */
  identity?: { tenantId: string | null; orgEmail: string | null; tenantName?: string | null }
}) {
  const hasIdentity = !!identity
  const tenantId = identity?.tenantId ?? null
  const orgEmail = identity?.orgEmail ?? null
  const [ready, setReady] = useState(false)
  // Layout effect + `ready`: the modifier's data effects must not fire before
  // the identity is in place (children's effects run before a parent's).
  useLayoutEffect(() => {
    if (!hasIdentity) { setReady(true); return }
    const restore = overrideActiveIdentity({ tenantId, orgEmail }, settingsPath)
    setReady(true)
    return () => { restore(); setReady(false) }
  }, [hasIdentity, tenantId, orgEmail])
  const managingAs = [identity?.tenantName, orgEmail].filter(Boolean).join(' · ')
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-auto" data-testid="share-pdapp-settings">
      {managingAs && (
        <div className="shrink-0 truncate border-b themed-border px-3 py-1.5 text-xs themed-text-muted" data-testid="share-pdapp-settings-identity">
          Managing as <span className="themed-text">{managingAs}</span>
        </div>
      )}
      {ready && <MiniAppSettingsModifier key={`${tenantId}:${orgEmail}`} folderId={folderId} folderName={folderName ?? undefined} suppressInstallGate />}
    </div>
  )
}
